import React, { useState } from 'react';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardMedia from '@mui/material/CardMedia';
import Chip from '@mui/material/Chip';
import Pagination from '@mui/material/Pagination';
import IconButton from '@mui/material/IconButton';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import TuneIcon from '@mui/icons-material/Tune';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import useLzFApiCall from '@lz/services/Api';
import { useLzOnMount } from '@lz/services/Hooks';
import { LzTOpenObject, LzTFunctionSimple, LzTMediaStateDataParams } from '@lz/typings/types';
import { lzFDivide, lzFBytesToSize, useLzFHideMediaBrowser } from '@lz/services/Utils';
import LzXToolbar, { LzTToolbarItem } from '@lz/components/commons/Toolbar';
import LzXFiltersDialog from '@lz/components/commons/FiltersDialog';
import LzXInputField from '@lz/components/forms/fields/InputField';
import LzXSelectField from '@lz/components/forms/fields/SelectField';
import LzMBase, { LzTBase } from '@lz/models/commons/Base';
import LzMMedia from '@lz/models/commons/Media';
import config from './config';
import LzXMediaUploader from './Uploader';
import LzXMediaInfo from './Info';

export interface LzPMediaBrowser {
  modelRef?: LzTBase;
  forSelection?: boolean;
  params?: LzTMediaStateDataParams;
  onSelect?: (rec: LzMBase) => void;
}

const LzXMediaBrowser = (props: LzPMediaBrowser) => {
  const modelRef = props.modelRef || LzMMedia;

  const getInitFilters = (): LzTOpenObject => {
    return {};
  };

  const [showFilters, setShowFilters] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [checkedIds, setCheckedIds] = useState<Array<number>>([]);
  const [filters, setFilters] = useState<LzTOpenObject>(getInitFilters());
  const [curPage, setCurPage] = useState(0);
  const [perPage, setPerPage] = useState(0);
  const [sortKey, setSortKey] = useState('');
  const [sortDir, setSortDir] = useState('');
  const [records, setRecords] = useState<LzMBase[]>([]);
  const [selectedMedia, setSelectedMedia] = useState<LzMBase | null>(null);
  const [total, setTotal] = useState(0);
  const [selectedTab, setSelectedTab] = useState('browse');

  const lzApiCall = useLzFApiCall();
  const lzFHideMediaBrowser = useLzFHideMediaBrowser();

  const loadRecords = async (
    runAfterSuccessStateChanged?: LzTFunctionSimple
  ) => {
    // Make params to send
    const params: LzTOpenObject = {
      _sort_order:
        typeof sortKey === 'undefined' || !sortKey || '' === sortKey
          ? ''
          : sortKey + ' ' + sortDir,
      _page_index: curPage + 1,
      _page_size: perPage,
      _show_deleted: showDeleted,
    };

    // Add filters to params if any
    for (const key in filters) {
      params[key] = filters[key];
    }

    // Make the api call and get the data
    const data = await lzApiCall(config.ident, params);

    // Load data if call was successful
    if (data && data.data) {
      setRecords(modelRef.parseArray(data.data, modelRef));
      setTotal(+(data.total || 0));
      setCurPage(+(data.current_page || 0) - 1);
      setPerPage(+(data.per_page || 0));
      setSortKey(data.sort_key || '');
      setSortDir(data.sort_dir || '');
      setCheckedIds([]);
      if (typeof runAfterSuccessStateChanged === 'function') {
        runAfterSuccessStateChanged();
      }
    }
  };

  useLzOnMount(() => {
    loadRecords();
  });

  const handleGroupActionIconClick = async (
    key: string,
    confirmation: string
  ) => {
    // If confirmation msg is provided then first confirm
    if (confirmation && confirmation !== '') {
      if (!window.confirm(confirmation)) {
        return;
      }
    }

    // Make the api call
    const data = await lzApiCall(
      config.ident + '/0',
      {
        _action: key,
        ids: checkedIds,
      },
      'patch'
    );

    // Load data if call was successful
    if (data) {
      loadRecords();
    }
  };

  const handleShowDeletedChange = (val: boolean) => {
    setShowDeleted(val);
    loadRecords();
  };

  const changeStatus = async (record: LzMBase) => {
    // Make the api call
    const data = await lzApiCall(
      config.ident + '/' + record.id,
      {
        _action: 'active',
        is_active: !record.is_active,
      },
      'patch'
    );

    // Load data if call was successful
    if (data) {
      loadRecords();
    }
  };

  const deleteRecord = async (id: number, confirmed?: boolean) => {
    if (!confirmed) {
      if (!window.confirm('Are you sure you want to delete this record?')) {
        return;
      }
    }

    // Make the api call
    const data = await lzApiCall(config.ident + '/' + id, null, 'delete');

    // Load data if call was successful
    if (data) {
      loadRecords();
    }
  };

  const deletedRecordAction = async (
    id: number,
    action: string,
    confirmed?: boolean
  ) => {
    if (!confirmed) {
      if (
        !window.confirm('Are you sure you want to ' + action + ' this record?')
      ) {
        return;
      }
    }

    // Make the api call
    const data = await lzApiCall(
      config.ident + '/' + id,
      { _action: action },
      'delete'
    );

    // Load data if call was successful
    if (data) {
      loadRecords();
    }
  };

  const onCheckAllClick = (checked: boolean) => {
    if (checked) {
      setCheckedIds(records.map((r) => r.id));
    } else {
      setCheckedIds([]);
    }
  };

  const onCheckClick = (checked: boolean, id: number) => {
    const updatedCheckedIds = checkedIds.slice();

    if (checked) {
      updatedCheckedIds.push(id);
    } else {
      updatedCheckedIds.splice(updatedCheckedIds.indexOf(id), 1);
    }

    setCheckedIds(updatedCheckedIds);
  };

  const onPageChange = (pageNo: number) => {
    setCurPage(pageNo);
    loadRecords();
  };

  const onUploadingSuccess = (
    medias: Array<LzMBase>,
    filesErrors: Array<string>
  ) => {
    if (!filesErrors || 0 === filesErrors.length) {
      setSelectedTab('browse');
      loadRecords();
    }
  };

  const onSelectMedia = (media: LzMBase) => {
    if (selectedMedia?.id === media.id) {
      setSelectedMedia(null);
    } else {
      setSelectedMedia(media);
    }
  };

  const handleActionIconClick = (key: string, rec: LzMBase) => {
    if ('select' === key) {
      if (props.onSelect) {
        props.onSelect(rec);
      } else {
        console.log('onSelect() not defined...');
      }
      lzFHideMediaBrowser();
    } else if ('status' === key) {
      changeStatus(rec);
    } else if ('delete' === key) {
      deleteRecord(rec.id);
    } else if ('deleted_restore' === key) {
      deletedRecordAction(rec.id, 'restore');
    } else if ('deleted_purge' === key) {
      deletedRecordAction(rec.id, 'purge');
    } else {
      console.log('TODO: Handle Action Icon Event', key, rec.id);
    }
  };

  const applyFilters = () => {
    loadRecords();
    setShowFilters(false);
  };

  const handleFiltersInput = (name: string, value: any) => {
    setFilters(Object.assign({}, filters, { [name]: value }));
  };

  const getToolbarItems = (): LzTToolbarItem[] => {
    const out: LzTToolbarItem[] = [];

    if (!props.forSelection) {
      if (checkedIds.length > 0) {
        out.push({
          label: 'Activate Selected',
          icon: <CheckIcon />,
          color: 'success',
          variant: 'contained',
          onClick: () =>
            handleGroupActionIconClick(
              'status_active',
              'Are you sure you want to activate all selected records?'
            ),
        });
        out.push({
          label: 'De-Activate Selected',
          icon: <CloseIcon />,
          color: 'warning',
          variant: 'contained',
          onClick: () =>
            handleGroupActionIconClick(
              'status_inactive',
              'Are you sure you want to de-activate all selected records?'
            ),
        });
        out.push({
          label: 'Delete Selected',
          icon: <DeleteIcon />,
          color: 'error',
          variant: 'contained',
          onClick: () =>
            handleGroupActionIconClick(
              'delete',
              'Are you sure you want to delete all selected records?'
            ),
        });
      } else {
        out.push({
          label: 'Refresh',
          icon: <RefreshIcon />,
          color: 'success',
          variant: 'contained',
          onClick: () => loadRecords(),
        });
        out.push({
          label: 'Show/Hide Filters',
          icon: <TuneIcon />,
          color: 'warning',
          variant: 'contained',
          onClick: () => setShowFilters(!showFilters),
        });
        if (showDeleted) {
          out.push({
            label: 'Hide Deleted Records',
            icon: <DeleteForeverIcon />,
            color: 'warning',
            variant: 'contained',
            onClick: () => handleShowDeletedChange(false),
          });
        } else {
          out.push({
            label: 'Show Deleted Records',
            icon: <DeleteIcon />,
            color: 'warning',
            variant: 'contained',
            onClick: () => handleShowDeletedChange(true),
          });
        }
      }
    }

    return out;
  };

  const getToolbarOtherStack = () => {
    if (props.forSelection) {
      return null;
    }

    const totalVisibleRows = records.length;
    const totalChecked = checkedIds.length;

    if (totalChecked > 0) {
      return (
        <Typography color='inherit' variant='subtitle1'>
          &nbsp; {totalChecked} selected
          <Tooltip title='Clear Selections'>
            <IconButton onClick={() => setCheckedIds([])}>
              <ClearAllIcon />
            </IconButton>
          </Tooltip>
        </Typography>
      );
    }

    return (
      <Typography>
        <FormControlLabel
          control={
            <Checkbox
              indeterminate={
                totalChecked > 0 && totalChecked < totalVisibleRows
              }
              checked={
                totalVisibleRows > 0 && totalChecked === totalVisibleRows
              }
              onChange={(ev) => onCheckAllClick(ev.target.checked)}
            />
          }
          label='Check All'
        />
      </Typography>
    );
  };

  if (!records) {
    return <CircularProgress />;
  }

  return (
    <Grid container spacing={1} className='lz-media-browser'>
      <Grid size={{ sm: 8 }}>
        <Paper className='lz-paper-box' variant='outlined'>
          <Tabs
            indicatorColor='primary'
            textColor='primary'
            value={selectedTab}
            onChange={(_, val) => setSelectedTab(val)}
          >
            <Tab value='browse' label='Files Library' />
            <Tab value='upload' label='Upload Files' />
            <Tab value='download' label='Download from Web' disabled />
          </Tabs>

          {'browse' === selectedTab && (
            <React.Fragment>
              <LzXToolbar
                icons
                highlighted={checkedIds.length > 0}
                items={getToolbarItems()}
                otherStack={getToolbarOtherStack()}
              />

              <LzXFiltersDialog
                show={showFilters}
                onClose={() => setShowFilters(false)}
                onReset={() => setFilters(getInitFilters())}
                onApply={() => applyFilters()}
              >
                <LzXInputField
                  fluid
                  label='Search image name'
                  name='name'
                  value={filters.name}
                  onChange={handleFiltersInput}
                />

                <LzXSelectField
                  label='Select file type'
                  firstOptionText='Any...'
                  fluid
                  name='file_type'
                  value={filters.file_type}
                  options={LzMMedia.getFileTypeOptions()}
                  onChange={handleFiltersInput}
                />

                <LzXSelectField
                  label='Select file visibility'
                  firstOptionValue={'0'}
                  firstOptionText='Any...'
                  fluid
                  name='is_private'
                  value={filters.is_private}
                  options={[
                    { value: '1', text: 'Private' },
                    { value: '2', text: 'Public' },
                  ]}
                  onChange={handleFiltersInput}
                />
              </LzXFiltersDialog>

              <Grid container spacing={1} style={{ marginTop: 12 }}>
                {records.map((rec, dex) => (
                  <Grid key={'file-' + dex} style={{ flexGrow: 1, maxWidth: 240 }}>
                    <Card
                      variant='outlined'
                      className={
                        'lz-media-card ' +
                        (rec.id === selectedMedia?.id ? 'selected' : '')
                      }
                      onClick={() => onSelectMedia(rec)}
                    >
                      <CardHeader
                        action={
                          props.forSelection ? (
                            ''
                          ) : (
                            <Checkbox
                              checked={checkedIds.indexOf(rec.id) >= 0}
                              onChange={(ev) =>
                                onCheckClick(ev.target.checked, rec.id)
                              }
                            />
                          )
                        }
                        title={rec.name}
                        subheader={lzFBytesToSize(rec.file_size)}
                      />
                      <CardMedia
                        image={rec.isImage ? rec.file_preview_path : null}
                        children={!rec.isImage ? rec.contentTypeIcon : null}
                        title={rec.name}
                      />
                      <Chip size='small' label={rec.category} />
                      <Chip
                        size='small'
                        className={
                          'status-chip ' +
                          (rec.deleted_at
                            ? 'danger'
                            : rec.is_active
                              ? 'success'
                              : 'warning')
                        }
                        label={
                          rec.deleted_at
                            ? 'deleted'
                            : rec.is_active
                              ? 'active'
                              : 'in-active'
                        }
                      />
                    </Card>
                  </Grid>
                ))}
              </Grid>

              <Pagination
                showFirstButton
                showLastButton
                style={{ marginTop: 12 }}
                page={curPage + 1}
                count={Math.ceil(lzFDivide(total, perPage))}
                onChange={(evt, val) => onPageChange(val - 1)}
              />
            </React.Fragment>
          )}

          {'upload' === selectedTab && (
            <React.Fragment>
              <LzXMediaUploader
                isPrivate={props.params?.is_private}
                category={props.params?.category}
                accept={LzMMedia.getContentTypeFromParams(
                  props.params?.content_type,
                  props.params?.file_type
                )}
                onUploaded={(medias, filesErrors) =>
                  onUploadingSuccess(medias, filesErrors)
                }
              />
            </React.Fragment>
          )}

          {'download' === selectedTab && (
            <React.Fragment>
              <p>this is download...</p>
            </React.Fragment>
          )}
        </Paper>
      </Grid>
      <Grid size={{ sm: 4 }}>
        <LzXMediaInfo
          forSelection={props.forSelection}
          record={selectedMedia}
          onActionIconClick={(key: string, rec: LzMBase) =>
            handleActionIconClick(key, rec)
          }
        />
      </Grid>
    </Grid>
  );
};

export default LzXMediaBrowser;
